import { Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { DisclaimerStrip } from "@/components/landing/DisclaimerStrip";
import { AREAS_DATA } from "@/lib/areas";
import { OWNER, PHONE_DIGITS, ROOMS } from "@/lib/constants";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function AreaPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const area = AREAS_DATA[slug];
  useSeo(
    area ? `${area.title} | Delhi Airport Stay & Transit Services` : "Delhi Airport Stay & Transit Services",
    area?.seoDesc
  );
  if (!area) return <Navigate to="/" replace />;

  const c = lang === "hi" && area.hi ? area.hi : area;
  const enquiryHref = `/?area=${slug}#enquiry`;
  const whatsappUrl = `https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent(
    `Hello ${OWNER}, I would like to enquire about a room near ${area.name}.`
  )}`;

  return (
    <div className="site-shell">
      <Navbar />
      <main>
        <section className="area-page-hero">
          <div>
            <div className="eyebrow" data-testid="area-eyebrow">
              <span className="eyebrow-dot" /> {t.areaCommon.eyebrow} · {area.num}
            </div>
            <h1 className="area-title" data-testid="area-title">
              <span className="mask-line">
                <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
                  {c.title}
                </motion.span>
              </span>
            </h1>
            <p className="hero-lede">{c.intro}</p>
            <div className="hero-actions">
              <a className="button button-gold" href={enquiryHref} data-testid="area-request-room-button">
                {t.areaCommon.requestRoom} <ArrowRight size={16} />
              </a>
              <a className="button button-ghost" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="area-whatsapp-button">
                <MessageCircle size={16} /> {t.areaCommon.whatsapp}
              </a>
            </div>
          </div>
          <motion.div
            className="hero-art"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.9, ease: EASE }}
            data-testid="area-photo"
          >
            <img src={area.img} alt={`${area.name} — illustrative visual`} />
            <div className="art-label">
              <span className="art-label-dot" />
              <span>
                <strong>{t.hero.labelTitle}</strong>
                <small>{t.hero.labelSub}</small>
              </span>
            </div>
          </motion.div>
        </section>

        <div className="facts-grid">
          {c.facts.map((fact, i) => (
            <motion.div
              className="fact-card"
              key={fact.k}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08, ease: EASE }}
              data-testid={`area-fact-${i + 1}`}
            >
              <small>{fact.k}</small>
              <strong>{fact.v}</strong>
              <p>{fact.p}</p>
            </motion.div>
          ))}
        </div>

        <section className="area-body">
          <div>
            <div className="eyebrow">{t.areaCommon.knowEyebrow}</div>
            <h2>{t.areaCommon.knowTitle}</h2>
            <ul className="know-list">
              {c.know.map((item, i) => (
                <li key={i} data-testid={`area-know-${i + 1}`}>
                  <CheckCircle2 size={16} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="eyebrow">{t.areaCommon.popularEyebrow}</div>
            <h2>{t.areaCommon.ratesFrom}</h2>
            <div className="mini-rooms">
              {ROOMS.slice(0, 3).map((room, i) => (
                <a className="mini-room" href={enquiryHref} key={room.name} data-testid={`area-room-${i + 1}`}>
                  <span>
                    <strong>{t.rooms.names[i]}</strong>
                    <small>{t.rooms.notes[i]}</small>
                  </span>
                  <em>
                    {room.price} <ArrowRight size={14} />
                  </em>
                </a>
              ))}
            </div>
            <p className="fine-print">{t.areaCommon.finePrint}</p>
          </div>
        </section>

        <DisclaimerStrip />
        <div className="area-cta">
          <h2>{t.areaCommon.bottomCta}</h2>
          <a className="button button-gold" href={enquiryHref} data-testid="area-bottom-cta">
            {t.areaCommon.sendEnquiry} <ArrowRight size={16} />
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
